/**
 * GlobalShortcuts — headless wiring of the app-wide keyboard shortcuts.
 *
 * This mounts (previously dead-code) shortcut infrastructure so it actually
 * works app-wide, additively and without touching any route:
 *   - Ctrl/⌘ K   → open the command palette
 *   - /          → focus global search (or open palette)
 *   - n          → open the command palette (create/new)
 *   - g d/r/g/…  → Gmail-style leader sequences that navigate to core modules
 *   - ?          → toggle the keyboard-shortcuts help overlay
 *
 * Rendered once inside the authenticated layout. Renders only the invisible
 * ShortcutsHelp dialog (opens on `?`), everything else is event listeners.
 */
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useKeyboardShortcuts,
  getDefaultShortcuts,
} from '@/hooks/use-keyboard-shortcuts'
import { ShortcutsHelp } from '@/components/ui/shortcuts-help'
import { useCommandPalette } from '@/components/ui/command-palette'

export function GlobalShortcuts() {
  const navigate = useNavigate()
  const { setOpen } = useCommandPalette()

  const shortcuts = useMemo(
    () => getDefaultShortcuts(navigate, (open: boolean) => setOpen(open)),
    [navigate, setOpen],
  )

  useKeyboardShortcuts(shortcuts)

  return <ShortcutsHelp shortcuts={shortcuts} />
}
